import React, { useState, useMemo } from 'react';
import imagePlaceholder from 'common/assets/image-placeholder.svg';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { OrderProduct, ProductApi, PricingTableObject, PurchaseTypes } from 'common/types';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import VariantSelectorDialog from 'components/VariantSelectorDialog';
import AvailabilityStatus from 'components/AvailabilityStatus';
import {
	PartialOrderProductFields,
	createOrderProductFromProductApi,
} from 'common/utils/newRentalUtils';
import { getTranslation } from 'common/modules/translations';
import { getLanguage } from 'services/localization/utils';
import { ProductTotalAvailability } from 'common/api/frontend/inventory_new';
import { getAllProductVariantIds, hasMultipleVariants } from 'common/modules/products/variants';
import { getProductImageSrc } from 'common/utils/productUtils';
import { useSelector } from 'react-redux';
import { getDefaultTimezone } from 'common/utils/dateUtils';
import * as ShopSelectors from 'selectors/ShopSelectors';

interface OwnProps {
	stockProduct: ProductApi;
	allStockProducts: ProductApi[];
	allOrderProducts: OrderProduct[];
	addProduct: (product: OrderProduct) => void;
	partialOrderProductFields: PartialOrderProductFields;
	savedPricingTables?: PricingTableObject;
	startDate: string;
	endDate: string;
	shopId: string;
	productAvailability: ProductTotalAvailability | undefined;
	loadingProductAvailability: boolean;
	availabilityNotLoadedForLongDuration: boolean;
	activeShopperSegment: string | undefined;
	currencyCode: string;
	taxExcluded: boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const ProductCard = (props: Props) => {
	const {
		classes,
		stockProduct,
		allStockProducts,
		partialOrderProductFields,
		productAvailability,
		availabilityNotLoadedForLongDuration,
		activeShopperSegment,
		currencyCode,
		taxExcluded,
	} = props;
	const { name } = stockProduct;
	const shopTimezone = useSelector(ShopSelectors.getShopTimeZone) ?? getDefaultTimezone();
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const [variantDialogOpen, setVariantDialogOpen] = useState(false);
	const setItemHasVariants = useMemo(() => {
		if (stockProduct.set && stockProduct.setProductIds) {
			return stockProduct.setProductIds.some((id) => {
				const match = allStockProducts.find((p) => p.id === id);
				if (match && hasMultipleVariants(match)) {
					return true;
				}
				return false;
			});
		}
		return false;
	}, [allStockProducts, stockProduct.set, stockProduct.setProductIds]);

	const handleCardClick = () => {
		if (hasMultipleVariants(stockProduct) && !variantDialogOpen) {
			setVariantDialogOpen(!variantDialogOpen);
		} else if (setItemHasVariants && !variantDialogOpen) {
			setVariantDialogOpen(!variantDialogOpen);
		} else if (!variantDialogOpen) {
			const {
				stockProduct,
				allStockProducts,
				partialOrderProductFields,
				savedPricingTables,
			} = props;
			const selectedVariant = stockProduct.variants.options[0];
			const productVariantIds = getAllProductVariantIds(stockProduct, allStockProducts, [
				selectedVariant.id,
			]);
			const orderProduct = createOrderProductFromProductApi({
				stockProduct,
				stockProductListForSetProducts: allStockProducts,
				partialOrderProductFields,
				savedPricingTables: savedPricingTables ?? {},
				currencyCode,
				taxExcluded,
				segment: activeShopperSegment ?? null,
				selectedVariant,
				productVariantIds,
				channel: 'ADMIN',
				purchaseType: PurchaseTypes.rental,
				timezone: shopTimezone,
				openingHours,
			});
			props.addProduct(orderProduct);
		}
	};

	return (
		<Card
			className={classes.productCard}
			onClick={handleCardClick}
			raised={false}
			data-intercom-target={props['data-intercom-target']}
		>
			<CardContent className={classes.cardContent}>
				<Typography variant="body1" className={classes.productName}>
					{getTranslation(name, getLanguage())}
				</Typography>
				{variantDialogOpen && (
					<VariantSelectorDialog
						open={variantDialogOpen}
						orderProducts={[]}
						allOrderProducts={props.allOrderProducts}
						onClose={() => setVariantDialogOpen(false)}
						stockProduct={stockProduct}
						allStockProducts={allStockProducts}
						partialOrderProductFields={partialOrderProductFields}
						addProduct={(orderProduct: OrderProduct) => props.addProduct(orderProduct)}
						savedPricingTables={props.savedPricingTables}
						removeProduct={() => null}
						startDate={props.startDate}
						endDate={props.endDate}
						availability={productAvailability?.availabilityRangesByVariant}
						quantities={productAvailability?.variantQuantities}
						fullAvailability={productAvailability?.productAvailability}
						fullQuantity={productAvailability?.totalQuantity}
						availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
						currencyCode={currencyCode}
						taxExcluded={taxExcluded}
						isLoadingAvailability={props.loadingProductAvailability}
					/>
				)}
			</CardContent>
			<CardMedia
				className={classes.media}
				image={getProductImageSrc(stockProduct) ?? imagePlaceholder}
				title={getTranslation(name, getLanguage())}
			/>
			<AvailabilityStatus
				availability={productAvailability?.productAvailabilityWithOrderProducts}
				quantity={productAvailability?.totalQuantity}
				isLoadingAvailability={props.loadingProductAvailability}
				availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
			/>
		</Card>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		productCard: {
			position: 'relative',
			minHeight: '100%',
			display: 'flex',
			flexDirection: 'column',
			borderRadius: '4px',
			border: `1px solid ${theme.palette.border.paper}`,
			marginTop: '4px',
			transition: 'background 0.3s',
			boxShadow: 'none',
		},
		cardContent: {
			flex: 1,
		},
		media: {
			// height: 100,
			display: 'flex',
			flex: 'none',
			paddingTop: 'calc(56.25% + 16px)', // 16:9
			backgroundSize: 'contain',
		},
		cardBody: {
			padding: 0,
			paddingTop: 'calc(56.25% + 16px)',
		},
		relative: {
			position: 'relative',
		},
		productName: {
			textAlign: 'center',
		},
		cardRoot: {
			'&:last-child': {
				paddingBottom: 0,
			},
		},
		productSelection: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			position: 'absolute',
			width: '100%',
			padding: '0px 8px',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			margin: 'auto',
		},
		flexColumn: {
			flexDirection: 'column',
		},
		additionalProducts: {
			textAlign: 'center',
			position: 'absolute',
			width: '100%',
			left: 0,
			right: 0,
			bottom: 0,
			margin: 'auto',
			background: theme.palette.primary.main,
		},
		addButton: {
			padding: 8,
			flex: '0 0 auto',
			minWidth: 0,
		},
		addUnit: {
			flex: '1 0 auto',
			minWidth: 32,
			maxWidth: 80,
		},
	});

export default withStyles(styles)(ProductCard);
